import { useCallback, useEffect, useState } from 'react';
import {
  fetchMineImpressions,
  type GuestImpression,
} from './guestImpressionsApi';
import { MediaGallery } from './media/MediaGallery';
import { AchievementsPreview } from './profile/AchievementsPreview';
import { ModerationRestrictionCard, XpModerationWorkspace } from './profile/ModerationXpSection';
import { ProfileActivitySection } from './profile/ProfileActivitySection';
import { ProfileVenuesSection } from './profile/ProfileVenuesSection';
import { RecentPhotosPreview } from './profile/RecentPhotosPreview';
import { SavedPlacesSection } from './profile/SavedPlacesSection';
import { VisitHistorySection } from './profile/VisitHistorySection';
import { isProfilePhotosPath, pushProfilePhotosPath } from './shellHistory';
import {
  fetchMyXpProgress,
  type MyXpResponse,
} from './socialApi';
import { UserAvatar } from './social/UserAvatar';
import { DesktopThreeColumnLayout } from './ui/DesktopThreeColumnLayout';
import { Icon } from './ui/Icons';
import './profile.css';
import './profile-layout.css';
import './impressions.css';
import './tz5-owner-qa.css';
import './profile-experience-v1.css';

export type SessionUser = {
  id: string;
  display_name: string | null;
  slug?: string | null;
  bio?: string | null;
  is_public?: boolean;
  avatar_url?: string | null;
  locale: string;
  timezone: string | null;
  status: string;
};

export type SessionResponse = {
  authenticated: boolean;
  user: SessionUser | null;
};

type CsrfResponse = {
  csrf_token: string;
};

type ProfileState = 'loading' | 'ready' | 'guest' | 'error';
type ProfileWorkspace = 'overview' | 'xp' | 'venues' | 'saved' | 'visits';

type GlobalLevelProgress = {
  enabled: boolean;
  level: number;
  status: string;
  next_status: string | null;
  xp_total: number;
  xp_current_level: number;
  xp_next_level: number;
  xp_progress_percent: number;
  is_max_level: boolean;
};

type ProfileXpResponse = MyXpResponse & {
  levels: GlobalLevelProgress | null;
};

type ProfileScreenProps = {
  onSessionChange?: (session: SessionResponse) => void;
  onCreateImpression?: (visitId: string, venueName: string) => void;
  onEditImpression?: (impressionId: string) => void;
  onOpenFriends?: () => void;
  onOpenSettings?: () => void;
  onOpenUser?: (userId: string) => void;
  onOpenVenue?: (venueId: string) => void;
  cityId?: string;
  cityName?: string;
  flash?: string | null;
};

const AUTH_RETURN_TARGET_KEY = 'hopper.authReturnTarget';
const AUTH_RETURN_URL_KEY = 'hopper.authReturnUrl';
const PENDING_SCAN_KEY = 'hopper.pendingFiscalQr';
const PROFILE_FLASH_KEY = 'hopper.profileFlash';

function profileQuery(): URLSearchParams {
  return new URL(window.location.href).searchParams;
}

function initialWorkspace(): ProfileWorkspace {
  return profileQuery().get('section') === 'xp' || profileQuery().has('moderation')
    ? 'xp'
    : 'overview';
}

function initialDecision(): string | null {
  return profileQuery().get('moderation')?.trim() || null;
}

async function readJson<T>(url: string): Promise<T> {
  const response = await fetch(url, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`${url} failed: ${response.status}`);
  }

  return response.json() as Promise<T>;
}

function ProfileLoadingSkeleton() {
  return (
    <main className="self-profile-page profile-experience-page" aria-label="Профиль Хоппера">
      <DesktopThreeColumnLayout
        className="self-profile-layout profile-experience-desktop"
        left={(
          <section className="profile-reference-card profile-left-identity profile-reference-skeleton" aria-hidden="true">
            <span className="social-skeleton social-skeleton--avatar is-profile" />
            <span className="social-skeleton social-skeleton--line is-medium" />
            <span className="social-skeleton social-skeleton--line is-short" />
            {Array.from({ length: 5 }, (_, index) => <span className="social-skeleton profile-nav-skeleton" key={index} />)}
          </section>
        )}
        right={(
          <section className="profile-reference-card profile-reference-skeleton profile-tall-skeleton" aria-hidden="true">
            {Array.from({ length: 5 }, (_, index) => <span className="social-skeleton social-skeleton--line" key={index} />)}
          </section>
        )}
      >
        <div className="self-profile-center" aria-hidden="true">
          <section className="profile-reference-card profile-reference-skeleton profile-tall-skeleton">
            {Array.from({ length: 5 }, (_, index) => <span className="social-skeleton social-skeleton--line" key={index} />)}
          </section>
        </div>
      </DesktopThreeColumnLayout>
      <div className="profile-mobile-loading" aria-hidden="true">
        <span />
        <span />
      </div>
    </main>
  );
}

function LevelProgress({ progress }: { progress: GlobalLevelProgress }) {
  const remainingXp = Math.max(0, progress.xp_next_level - progress.xp_total);
  const percent = Math.max(0, Math.min(100, progress.xp_progress_percent));

  return (
    <div className="profile-level-progress">
      <div>
        <strong>Уровень {progress.level}</strong>
        <span>{progress.xp_total.toLocaleString('ru-RU')} / {progress.xp_next_level.toLocaleString('ru-RU')} XP</span>
      </div>
      <div className="profile-level-progress__bar" aria-label={`Прогресс уровня ${percent}%`}>
        <span style={{ width: `${percent}%` }} />
      </div>
      <small>{progress.is_max_level ? 'Максимальный уровень' : `До следующего уровня: ${remainingXp.toLocaleString('ru-RU')} XP`}</small>
      {progress.next_status && <small>Следующий статус: {progress.next_status}</small>}
    </div>
  );
}

export function ProfileScreen({
  onSessionChange,
  onCreateImpression,
  onOpenFriends,
  onOpenSettings = () => window.location.assign('/profile/settings'),
  onOpenVenue,
  cityName = 'Город',
  flash = null,
}: ProfileScreenProps) {
  const [state, setState] = useState<ProfileState>('loading');
  const [session, setSession] = useState<SessionResponse | null>(null);
  const [progress, setProgress] = useState<ProfileXpResponse | null>(null);
  const [impressions, setImpressions] = useState<GuestImpression[]>([]);
  const [workspace, setWorkspace] = useState<ProfileWorkspace>(initialWorkspace);
  const [decisionId, setDecisionId] = useState<string | null>(initialDecision);
  const [photosRoute, setPhotosRoute] = useState(() => isProfilePhotosPath());
  const [photoTotal, setPhotoTotal] = useState(0);
  const [loggingOut, setLoggingOut] = useState(false);
  const [actionFlash, setActionFlash] = useState<string | null>(() => {
    const value = sessionStorage.getItem(PROFILE_FLASH_KEY);
    sessionStorage.removeItem(PROFILE_FLASH_KEY);
    return value;
  });

  const loadProfile = useCallback(async () => {
    setState('loading');

    try {
      const nextSession = await readJson<SessionResponse>('/api/v1/auth/session');
      setSession(nextSession);
      onSessionChange?.(nextSession);

      if (!nextSession.authenticated || !nextSession.user) {
        setProgress(null);
        setImpressions([]);
        setState('guest');
        return;
      }

      const returnUrl = sessionStorage.getItem(AUTH_RETURN_URL_KEY)
        ?? localStorage.getItem(AUTH_RETURN_URL_KEY);
      if (returnUrl?.startsWith('/profile')) {
        sessionStorage.removeItem(AUTH_RETURN_URL_KEY);
        localStorage.removeItem(AUTH_RETURN_URL_KEY);
        window.history.replaceState(window.history.state, '', returnUrl);
        setWorkspace(initialWorkspace());
        setDecisionId(initialDecision());
      }

      const [xpResponse, impressionsResponse] = await Promise.all([
        fetchMyXpProgress(),
        fetchMineImpressions(),
      ]);

      setProgress(xpResponse as ProfileXpResponse);
      setImpressions(impressionsResponse.data);
      setState('ready');
    } catch {
      setState('error');
    }
  }, [onSessionChange]);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    const syncFromLocation = () => {
      setPhotosRoute(isProfilePhotosPath());
      setWorkspace(initialWorkspace());
      setDecisionId(initialDecision());
    };
    const syncFromProfilePhotoEvent = (event: Event) => {
      const full = (event as CustomEvent<{ full?: boolean }>).detail?.full;
      setPhotosRoute(typeof full === 'boolean' ? full : isProfilePhotosPath());
    };

    window.addEventListener('popstate', syncFromLocation);
    window.addEventListener('hopper:profile-photo-view', syncFromProfilePhotoEvent);

    return () => {
      window.removeEventListener('popstate', syncFromLocation);
      window.removeEventListener('hopper:profile-photo-view', syncFromProfilePhotoEvent);
    };
  }, []);

  const setProfileWorkspace = useCallback((next: ProfileWorkspace, nextDecision: string | null = null) => {
    setWorkspace(next);
    setDecisionId(nextDecision);

    const url = new URL(window.location.href);
    url.searchParams.delete('venue_id');
    if (next === 'xp') {
      url.searchParams.set('section', 'xp');
    } else {
      url.searchParams.delete('section');
    }
    if (nextDecision) {
      url.searchParams.set('moderation', nextDecision);
    } else {
      url.searchParams.delete('moderation');
    }
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  }, []);

  const startYandexLogin = useCallback(() => {
    sessionStorage.setItem(AUTH_RETURN_TARGET_KEY, 'profile');
    if (profileQuery().get('section') === 'xp' || profileQuery().has('moderation')) {
      sessionStorage.setItem(AUTH_RETURN_URL_KEY, `${window.location.pathname}${window.location.search}`);
    }
    sessionStorage.removeItem(PENDING_SCAN_KEY);
    window.location.assign('/api/v1/auth/yandex/redirect');
  }, []);

  const logout = useCallback(async () => {
    setLoggingOut(true);
    setActionFlash(null);

    try {
      const csrf = await readJson<CsrfResponse>('/api/v1/auth/csrf');
      const response = await fetch('/api/v1/auth/logout', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-CSRF-TOKEN': csrf.csrf_token,
        },
      });

      if (!response.ok) {
        throw new Error(`Logout failed: ${response.status}`);
      }

      const guestSession: SessionResponse = { authenticated: false, user: null };
      setSession(guestSession);
      setProgress(null);
      setImpressions([]);
      onSessionChange?.(guestSession);
      setState('guest');
    } catch {
      setActionFlash('Не удалось выйти из Хоппера');
    } finally {
      setLoggingOut(false);
    }
  }, [onSessionChange]);

  if (state === 'loading') {
    return <ProfileLoadingSkeleton />;
  }

  if (state === 'guest') {
    return (
      <main className="profile-page profile-page--state" aria-label="Профиль Хоппера">
        <div className="profile-login-card">
          <div className="profile-login-icon"><Icon name="profile" /></div>
          <span className="profile-eyebrow">Профиль Хоппера</span>
          <h1>Войдите, чтобы собрать свою городскую историю</h1>
          <p>Подтверждённые визиты, XP и впечатления будут собраны здесь в одном месте.</p>
          <button className="profile-primary-button" type="button" onClick={startYandexLogin}>
            Войти через Яндекс
          </button>
        </div>
      </main>
    );
  }

  if (state === 'error' || !progress || !session?.user) {
    return (
      <main className="profile-page profile-page--state" aria-label="Профиль Хоппера">
        <div className="profile-state-card">
          <strong>Не удалось обновить профиль</strong>
          <span>Проверьте соединение и попробуйте загрузить данные ещё раз.</span>
          <button className="profile-primary-button" type="button" onClick={() => void loadProfile()}>
            Попробовать снова
          </button>
        </div>
      </main>
    );
  }

  const user = session.user;
  const userName = user.display_name?.trim() || 'Гость Хоппера';
  const xpTotal = progress.xp.summary.xp_total ?? progress.xp.summary.total_xp;
  const globalLevel = progress.levels;
  const profileCity = progress.xp.cities[0]?.city.name ?? cityName;
  const impressionVisitIds = new Set(
    impressions.map((item) => item.visit_id).filter((value): value is string => Boolean(value)),
  );
  const openVenue = onOpenVenue ?? (() => undefined);
  const openUser = (userId: string) => window.location.assign(`/profile/${encodeURIComponent(userId)}`);
  const visibleFlash = flash ?? actionFlash;

  const updateAvatar = (avatarUrl: string | null) => {
    const nextSession: SessionResponse = {
      ...session,
      user: { ...user, avatar_url: avatarUrl },
    };
    setSession(nextSession);
    onSessionChange?.(nextSession);
  };

  if (photosRoute) {
    return (
      <main className="social-page media-gallery-route profile-photos-route" aria-label="Мои фото">
        <button
          className="social-back"
          type="button"
          onClick={() => window.history.length > 1 ? window.history.back() : window.location.assign('/profile')}
        >
          ← Назад
        </button>
        <section className="user-profile-card profile-media-section is-profile-photo-gallery-card">
          <MediaGallery
            mode="user_profile"
            entityId={user.id}
            canEdit
            onOpenVenue={openVenue}
            onAvatarChange={({ avatarUrl }) => updateAvatar(avatarUrl)}
          />
        </section>
      </main>
    );
  }

  const leftColumn = (
    <aside className="profile-reference-card profile-experience-left">
      <UserAvatar displayName={userName} avatarUrl={user.avatar_url} size={88} />
      <div className="profile-experience-identity">
        <h1>{userName}</h1>
        {user.slug && <span>@{user.slug}</span>}
        <span>{profileCity}</span>
        {user.bio && <p>{user.bio}</p>}
        {globalLevel && <strong>{globalLevel.status}</strong>}
        <small>{xpTotal.toLocaleString('ru-RU')} XP</small>
      </div>

      {globalLevel && <LevelProgress progress={globalLevel} />}
      <button className="profile-xp-history-link" type="button" onClick={() => setProfileWorkspace('xp')}>
        История XP
      </button>

      <nav className="profile-experience-nav" aria-label="Разделы профиля">
        <button type="button" className={workspace === 'overview' ? 'is-active' : ''} onClick={() => setProfileWorkspace('overview')}>Обзор</button>
        <button type="button" className={workspace === 'xp' ? 'is-active' : ''} onClick={() => setProfileWorkspace('xp')}>XP и модерация</button>
        <button type="button" className={workspace === 'venues' ? 'is-active' : ''} onClick={() => setProfileWorkspace('venues')}>Мои заведения</button>
        <button type="button" className={workspace === 'saved' ? 'is-active' : ''} onClick={() => setProfileWorkspace('saved')}>Хочу посетить</button>
        <button type="button" className={workspace === 'visits' ? 'is-active' : ''} onClick={() => setProfileWorkspace('visits')}>История посещений</button>
        <button type="button" onClick={onOpenSettings}>Настройки</button>
      </nav>

      <div className="profile-experience-destinations" aria-label="Отдельные разделы профиля">
        <button type="button" onClick={pushProfilePhotosPath}>Фото</button>
        <button type="button" onClick={() => window.location.assign('/profile/achievements')}>Достижения</button>
        {onOpenFriends && <button type="button" onClick={onOpenFriends}>Подписки</button>}
      </div>

      <button className="profile-logout-link" type="button" disabled={loggingOut} onClick={() => void logout()}>
        {loggingOut ? 'Выходим…' : 'Выйти из Хоппера'}
      </button>
    </aside>
  );

  const overviewWorkspace = (
    <div className="profile-workspace-stack">
      {visibleFlash && <div className="profile-reference-flash">{visibleFlash}</div>}
      <ModerationRestrictionCard onOpenDecision={(id) => setProfileWorkspace('xp', id)} />

      <section className="profile-reference-card profile-overview-hero" aria-label="Обзор профиля">
        <div className="profile-overview-hero__top">
          <div className="profile-overview-hero__identity">
            <UserAvatar displayName={userName} avatarUrl={user.avatar_url} size={88} />
            <div className="profile-overview-hero__copy">
              <h1>{userName}</h1>
              {user.slug && <span>@{user.slug}</span>}
              {user.bio && <p>{user.bio}</p>}
              <span>{profileCity}</span>
            </div>
          </div>
          {globalLevel && (
            <div className="profile-overview-hero__status">
              <span aria-hidden="true"><Icon name="profile" /></span>
              <strong>{globalLevel.status}</strong>
            </div>
          )}
        </div>

        {globalLevel && (
          <div className="profile-overview-hero__progress">
            <LevelProgress progress={globalLevel} />
            <button className="profile-xp-history-link" type="button" onClick={() => setProfileWorkspace('xp')}>
              История XP →
            </button>
          </div>
        )}

        <div className="profile-overview-stats profile-overview-stats--in-hero" aria-label="Статистика профиля">
          <div><strong>{progress.stats.visits_count.toLocaleString('ru-RU')}</strong><span>Посещения</span></div>
          <div><strong>{progress.stats.impressions_count.toLocaleString('ru-RU')}</strong><span>Впечатления</span></div>
          <div><strong>{progress.stats.discovered_count.toLocaleString('ru-RU')}</strong><span>Открыто мест</span></div>
          <div><strong>{photoTotal.toLocaleString('ru-RU')}</strong><span>Фото</span></div>
        </div>
      </section>

      <AchievementsPreview />
      <RecentPhotosPreview userId={user.id} onVisibleTotalChange={setPhotoTotal} />
      <ProfileActivitySection
        includeAchievements={false}
        initialLimit={6}
        onOpenUser={openUser}
        onOpenVenue={openVenue}
      />
    </div>
  );

  const centerColumn = (
    <div className="self-profile-center profile-experience-center" aria-live="polite">
      {workspace === 'overview' && overviewWorkspace}
      {workspace === 'xp' && <XpModerationWorkspace initialDecisionId={decisionId} />}
      {workspace === 'venues' && <ProfileVenuesSection limit={50} onOpenVenue={openVenue} />}
      {workspace === 'saved' && <SavedPlacesSection limit={50} onOpenVenue={openVenue} />}
      {workspace === 'visits' && (
        <VisitHistorySection
          impressionVisitIds={impressionVisitIds}
          onCreateImpression={onCreateImpression}
          onOpenVenue={openVenue}
        />
      )}
    </div>
  );

  const rightColumn = (
    <aside className="profile-experience-right">
      <ProfileVenuesSection compact limit={3} onOpenVenue={openVenue} onShowAll={() => setProfileWorkspace('venues')} />
      <SavedPlacesSection compact limit={3} onOpenVenue={openVenue} onShowAll={() => setProfileWorkspace('saved')} />
    </aside>
  );

  return (
    <main className="self-profile-page profile-experience-page" aria-label="Профиль Хоппера">
      <section className="profile-reference-card profile-mobile-identity">
        <div className="profile-mobile-identity__row">
          <UserAvatar displayName={userName} avatarUrl={user.avatar_url} size={64} />
          <div>
            <h1>{userName}</h1>
            {user.slug && <span>@{user.slug}</span>}
            {globalLevel && <strong>{globalLevel.status} · Уровень {globalLevel.level}</strong>}
          </div>
        </div>
        {globalLevel && <LevelProgress progress={globalLevel} />}
        <ModerationRestrictionCard onOpenDecision={(id) => setProfileWorkspace('xp', id)} />

        <nav className="profile-mobile-local-nav" aria-label="Разделы профиля">
          <button
            type="button"
            className={workspace === 'overview' || workspace === 'venues' ? 'is-active' : ''}
            onClick={() => setProfileWorkspace('overview')}
          >
            Места
          </button>
          <button type="button" className={workspace === 'xp' ? 'is-active' : ''} onClick={() => setProfileWorkspace('xp')}>XP</button>
          <button type="button" onClick={pushProfilePhotosPath}>Фото</button>
          <button type="button" onClick={() => window.location.assign('/profile/achievements')}>Достижения</button>
        </nav>
        <button type="button" className="profile-mobile-saved-link" onClick={() => setProfileWorkspace('saved')}>Хочу посетить</button>
      </section>

      <div className="profile-mobile-workspace">
        {workspace === 'xp' ? (
          <XpModerationWorkspace initialDecisionId={decisionId} />
        ) : workspace === 'saved' ? (
          <SavedPlacesSection limit={50} onOpenVenue={openVenue} />
        ) : workspace === 'venues' ? (
          <ProfileVenuesSection limit={50} onOpenVenue={openVenue} />
        ) : (
          <>
            <RecentPhotosPreview userId={user.id} />
            <ProfileVenuesSection compact limit={5} onOpenVenue={openVenue} onShowAll={() => setProfileWorkspace('venues')} />
            <ProfileActivitySection
              includeAchievements={false}
              initialLimit={5}
              onOpenUser={openUser}
              onOpenVenue={openVenue}
            />
          </>
        )}
      </div>

      <DesktopThreeColumnLayout
        className="self-profile-layout profile-experience-desktop"
        left={leftColumn}
        right={rightColumn}
      >
        {centerColumn}
      </DesktopThreeColumnLayout>
    </main>
  );
}
